import React, { useState } from 'react';
import {
  DeleteButton,
  EditButton,
  SaveButton,
  Toolbar,
  TopToolbar,
  useGetOne,
  useNotify,
  useRecordContext,
  useTranslate,
  useRedirect,
} from 'react-admin';
import { Funeral } from '../types';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Theme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { FuneralCars } from './FuneralCars';
import WorkOrderButton from './WorkOrderButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StartDecButton from './StartDecButton';
import PhysicalMailButton from './PhysicalMailButton';
import { cloneDefaultLines, urnLinesFor } from 'paperman-contract-defaults';
import { FuneralOwnerSelector } from './FuneralOwnerSelector';
import { CopyCaseEmailButton } from './CopyCaseEmailButton';

const FuneralShowToolBar = () => {
  const record = useRecordContext<Funeral>();
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (isXSmall) {
    return (
      <TopToolbar>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label="more actions"
          aria-controls={open ? 'actions-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id="actions-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'actions-button',
          }}
        >
          <MenuItem onClick={handleClose}>
            {record && <StartDecButton funeralId={record.id} />}
          </MenuItem>
          <MenuItem onClick={handleClose}>
            <CreateContractButton mobile />
          </MenuItem>
          <MenuItem onClick={handleClose}>
            <PhysicalMailButton />
          </MenuItem>
          {record && (
            <MenuItem onClick={handleClose}>
              <CopyCaseEmailButton funeralId={record.id} mobile />
            </MenuItem>
          )}
          <MenuItem onClick={handleClose}>
            <EditButton size="small" label="Edit" />
          </MenuItem>
        </Menu>
      </TopToolbar>
    );
  }

  return (
    <TopToolbar>
      <FuneralOwnerSelector />
      {record && <CopyCaseEmailButton funeralId={record.id} />}
      <EditButton size="medium" />
    </TopToolbar>
  );
};

const CreateContractButton = ({ mobile = false }: { mobile?: boolean }) => {
  const record = useRecordContext<Funeral>();
  const translate = useTranslate();
  let disableCreate = false;
  const notify = useNotify();
  const redirect = useRedirect();

  if (record && record.primary_client_id === null) {
    disableCreate = true;
  }

  if (disableCreate) {
    return (
      <Button
        onClick={() =>
          notify(translate('resources.contracts.primary_client_required'), {
            type: 'error',
          })
        }
        startIcon={mobile ? undefined : <AddIcon />}
        aria-label={translate('resources.contracts.new')}
        fullWidth={mobile}
        sx={mobile ? { justifyContent: 'flex-start' } : undefined}
      >
        {mobile && <AddIcon sx={{ mr: 1 }} />}
        {translate('resources.contracts.name', { smart_count: 1 })}
      </Button>
    );
  }

  const handleCreateContract = () => {
    if (!record) {
      return;
    }

    redirect('/contracts/create', undefined, undefined, undefined, {
      record: {
        funeral_id: record.id,
        client_id: record.primary_client_id,
        lines: [
          ...cloneDefaultLines('at', record.region),
          ...urnLinesFor(record.urn_choice, record.region),
        ],
        is_preneed: false,
        is_monthly: false,
        monthly_payment_amount: null,
      },
    });
  };

  return record ? (
    <Button
      onClick={handleCreateContract}
      startIcon={mobile ? undefined : <AddIcon />}
      aria-label={translate('resources.contracts.new')}
      fullWidth={mobile}
      sx={mobile ? { justifyContent: 'flex-start' } : undefined}
    >
      {mobile && <AddIcon sx={{ mr: 1 }} />}
      {translate('resources.contracts.name', { smart_count: 1 })}
    </Button>
  ) : null;
};

export default FuneralShowToolBar;
